/**
 * SignInPage — route wrapper at /signin.
 *
 * Ported from Yan-Vada Hero Pilot/components/auth-screens.jsx SignInB.
 * Split layout: neon-at-sunset left panel + dark form right panel.
 * Chosen variant: B (matches NDAGate dark-neon aesthetic).
 *
 * Real auth starts through Cloudflare Pages Functions. Supabase sessions are held
 * in HttpOnly cookies; the browser only receives safe member state.
 */

import { useEffect, useState, type ChangeEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { beginFullSignOutAndRestart, fullSignOutUrl } from '../lib/fullSignOut'
import NeonWordmark from '../components/NeonWordmark'
import {
  AppleMark,
  GoogleMark,
  MagicLinkMark,
  AuthField,
  SSOButton,
  AuthDivider,
} from '../components/auth/shared'

const OAUTH_SIGNIN_ENABLED = false
const APPLE_SIGNIN_ENABLED = false
const RATE_LIMIT_COOLDOWN_SECONDS = 2 * 60
const RATE_LIMIT_COOLDOWN_KEY = 'yan_vada_auth_cooldown_until'

const ERROR_MESSAGES: Record<string, string> = {
  auth_not_configured: 'Email sign-in is not configured on this deployment yet. The required Supabase environment variables are missing.',
  auth_callback: 'The sign-in callback failed. Try again or request a fresh link.',
  auth_email_provider_failed: 'Email sign-in is not delivering right now. Try again shortly or contact the team.',
  auth_email_rate_limited: 'Too many sign-in links were requested. Wait a couple of minutes before trying again. Supabase may continue throttling for longer. Use only the newest email link.',
  auth_email_send_failed: 'Unable to send the sign-in link. Try again shortly.',
  auth_start_failed: 'Unable to start sign-in. Try again shortly.',
  auth_redirect_not_allowed: 'Email sign-in is not configured for this deployment yet.',
  access_denied: 'Central Yan recognized this identity, but this account is not active for Yan-Vada yet.',
  email_required: 'Enter the email connected to your invitation first.',
  invalid_auth_attempt: 'This sign-in attempt expired. Start again from the Yan-Vada sign-in page.',
  invalid_link: 'This sign-in link is invalid. Request a fresh link.',
  invalid_unified_token: 'Central Yan returned an invalid sign-in token. Start again from Yan-Vada sign-in.',
  invalid_state: 'This sign-in attempt no longer matches your browser session. Start again from Yan-Vada sign-in.',
  local_member_blocked: 'This Yan-Vada member account is blocked.',
  local_member_pending: 'Central Yan accepted the sign-in, but this Yan-Vada member account is still pending approval.',
  local_member_required: 'Central Yan accepted the sign-in, but there is no matching Yan-Vada member account yet.',
  missing_auth_attempt: 'This sign-in attempt is missing browser context. Start again from Yan-Vada sign-in.',
  missing_code: 'The sign-in callback was missing its code. Request a fresh link.',
  missing_email: 'The sign-in provider did not return an email address.',
  missing_token: 'This email link is missing its sign-in token. Request a fresh link.',
  member_sync: 'Signed in, but member access could not be synced.',
  otp_expired: 'This sign-in link expired. Request a fresh link.',
  provider_disabled: 'Google and Apple sign-in are disabled until central auth.yan.lk provider tests pass.',
  unified_auth_disabled: 'Central Yan sign-in is not enabled for this deployment yet.',
  unified_auth_failed: 'Central Yan sign-in failed. Try again shortly or contact the team.',
  unified_auth_not_configured: 'Central Yan sign-in is not configured on this deployment yet.',
  unified_token_exchange_failed: 'Central Yan sign-in could not complete. Try again shortly.',
  unsupported_link_type: 'This email link type is not supported. Request a fresh sign-in link.',
  unsupported_provider: 'That sign-in method is not available yet.',
}

function authErrorMessage(error: string | null) {
  if (!error) return null
  return ERROR_MESSAGES[error] ?? error
}

function formatCooldown(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  if (minutes <= 0) return `${remainder}s`
  return `${minutes}:${String(remainder).padStart(2, '0')}`
}

function safeNextPath(value: string | null) {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/'
}

function readCooldownSeconds() {
  try {
    const until = Number(localStorage.getItem(RATE_LIMIT_COOLDOWN_KEY) ?? 0)
    return Math.max(0, Math.ceil((until - Date.now()) / 1000))
  } catch {
    return 0
  }
}

function writeCooldown(seconds: number) {
  try {
    localStorage.setItem(RATE_LIMIT_COOLDOWN_KEY, String(Date.now() + seconds * 1000))
  } catch {
    /* noop */
  }
}

function clearCooldown() {
  try {
    localStorage.removeItem(RATE_LIMIT_COOLDOWN_KEY)
  } catch {
    /* noop */
  }
}

export default function SignInPage() {
  const [searchParams] = useSearchParams()
  const { isLoggedIn, loading, signInWithGoogle, signInWithApple, requestMagicLink } = useAuth()
  const nextPath = safeNextPath(searchParams.get('next'))
  const signedInRedirect = nextPath === '/signin' ? '/' : nextPath
  const legacyEmailEnabled = searchParams.get('legacy') === '1'
  const freshMode = searchParams.get('fresh') === '1'
  const [email, setEmail] = useState(() => searchParams.get('email')?.trim().toLowerCase() ?? '')
  const [message, setMessage] = useState<string | null>(
    freshMode
      ? 'Yan-Vada browser state was cleared. Continue with Yan to choose the account again.'
      : searchParams.get('status') === 'pending'
        ? 'Your account is pending approval.'
        : null
  )
  const [error, setError] = useState<string | null>(() => authErrorMessage(searchParams.get('error')))
  const [submitting, setSubmitting] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(readCooldownSeconds)

  useEffect(() => {
    if (!window.location.hash) return
    const hashParams = new URLSearchParams(window.location.hash.slice(1))
    const hashError = hashParams.get('error_code') || hashParams.get('error')
    if (hashError) setError(authErrorMessage(hashError))
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
  }, [])

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined
    const timer = window.setInterval(() => {
      setCooldownSeconds(seconds => {
        const next = Math.max(0, seconds - 1)
        if (next === 0) clearCooldown()
        return next
      })
    }, 1000)
    return () => window.clearInterval(timer)
  }, [cooldownSeconds])

  const startCooldown = () => {
    writeCooldown(RATE_LIMIT_COOLDOWN_SECONDS)
    setCooldownSeconds(RATE_LIMIT_COOLDOWN_SECONDS)
  }

  const handleCentralSignIn = () => {
    setSubmitting(true)
    setError(null)
    const startUrl = new URL('/api/auth/unified/start', window.location.origin)
    startUrl.searchParams.set('next', nextPath)
    if (freshMode) startUrl.searchParams.set('force', '1')
    window.location.href = `${startUrl.pathname}${startUrl.search}`
  }

  const handleFullSignOut = async (event?: ReactMouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault()
    setSubmitting(true)
    setError(null)
    setMessage('Clearing this browser and reopening Yan sign-in...')
    await beginFullSignOutAndRestart(nextPath)
  }

  const handleMagicLink = async () => {
    if (!email || cooldownSeconds > 0) return
    setSubmitting(true)
    setError(null)
    setMessage(null)
    try {
      const result = await requestMagicLink(email, nextPath)
      if (result.error) {
        if (result.error === 'auth_email_rate_limited') startCooldown()
        setError(authErrorMessage(result.error) ?? result.error)
      } else if (result.status === 'pending') {
        startCooldown()
        setMessage('Access request received. This email is pending approval. Do not keep requesting links.')
      } else {
        startCooldown()
        setMessage('Sign-in link sent. Open the newest email only. Older links may fail.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to request sign-in link')
    } finally {
      setSubmitting(false)
    }
  }

  const handleGoogle = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await signInWithGoogle(nextPath)
    } catch (err) {
      setSubmitting(false)
      setError(err instanceof Error ? err.message : 'Unable to start Google sign-in')
    }
  }

  const handleApple = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await signInWithApple(nextPath)
    } catch (err) {
      setSubmitting(false)
      setError(err instanceof Error ? err.message : 'Unable to start Apple sign-in')
    }
  }

  if (loading) {
    return (
      <div className="signin-page">
        <div className="signin-form">
          <h1 className="signin-heading">Yan-Vada Access</h1>
          <p className="signin-sub">Checking your session...</p>
        </div>
      </div>
    )
  }

  if (isLoggedIn && !freshMode) return <Navigate to={signedInRedirect} replace />

  return (
    <div className="signin-page">
      {/* left — neon-at-sunset brand panel */}
      <div className="signin-brand">
        <div className="signin-dusk-wash" />
        <div className="signin-brand-top">
          <Link to="/" className="signin-brand-label">Yan-Vada · යන්-වාද</Link>
        </div>
        <div className="signin-brand-center">
          <Link to="/" className="signin-brand-wordmark" aria-label="Go to Yan-Vada home">
            <NeonWordmark height={120} id="signin-wordmark" />
          </Link>
        </div>
        <div className="signin-brand-bottom">
          <p className="signin-brand-copy">
            Eight episodes. One balcony. Sign in for member prep, proposals, and session tools.
          </p>
          <span className="signin-brand-meta">Mount Lavinia &middot; Season 01</span>
        </div>
      </div>

      {/* right — form */}
      <div className="signin-form">
        <h1 className="signin-heading">Yan-Vada Access</h1>
        <p className="signin-sub">Sign in to continue to Yan-Vada.</p>
        <p className="signin-sub">For invited guests, partners, stakeholders, and team members connected to Yan and Yan-Vada.</p>

        {OAUTH_SIGNIN_ENABLED && (
          <>
            <div className="signin-sso-group">
              <SSOButton dark icon={<GoogleMark />} onClick={handleGoogle}>Continue with Google</SSOButton>
              {APPLE_SIGNIN_ENABLED && (
                <SSOButton dark icon={<AppleMark />} onClick={handleApple}>Continue with Apple</SSOButton>
              )}
            </div>

            <div className="signin-divider">
              <AuthDivider dark label="or" />
            </div>
          </>
        )}

        <div className="signin-email-group">
          <button
            className="btn btn-primary signin-magic-btn"
            onClick={handleCentralSignIn}
            disabled={submitting}
          >
            <MagicLinkMark dark /> {submitting ? 'Opening Yan sign-in...' : 'Continue with Yan'}
          </button>
          <p className="signin-password-hint">
            You can confirm or switch accounts on auth.yan.lk.
          </p>
          <a
            href={fullSignOutUrl(nextPath)}
            className="signin-full-signout"
            onClick={handleFullSignOut}
            aria-disabled={submitting}
          >
            Clear this browser and choose a different Yan account
          </a>
          {legacyEmailEnabled && (
            <>
              <AuthField dark label="Email" type="email" placeholder="you@company.com" value={email} onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} />
              <button
                className="btn btn-secondary signin-magic-btn"
                onClick={handleMagicLink}
                disabled={submitting || !email || cooldownSeconds > 0}
              >
                <MagicLinkMark dark /> {submitting ? 'Requesting link...' : cooldownSeconds > 0 ? `Try again in ${formatCooldown(cooldownSeconds)}` : 'Use legacy email link'}
              </button>
            </>
          )}
          {message && <p className="signin-password-hint" role="status">{message}</p>}
          {error && <p className="signin-password-hint" role="alert" style={{ color: '#C57060' }}>{error}</p>}
          {APPLE_SIGNIN_ENABLED && (
            <p className="signin-password-hint">
              Use the same email your invitation was sent to. If you continue with Apple, please do not choose Hide My Email. Private relay addresses cannot be matched to your invitation automatically.
            </p>
          )}
        </div>

        <div className="signin-footer">
          Need access?{' '}
          <Link to="/request-access" className="signin-footer-link">Request an invite</Link>
        </div>
      </div>
    </div>
  )
}
